import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import { CADENCES } from './notifications.data.js';
import type { CadenceId } from './notifications.types.js';

export function CadencePickerInline({
  value,
  onChange,
  disabled = false,
}: {
  value: CadenceId;
  onChange: (value: CadenceId) => void;
  disabled?: boolean;
}): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;

  const label = CADENCES.find((cadence) => cadence.id === value)?.label ?? value;

  return (
    <>
      <Box
        component="button"
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          height: 32,
          px: 2,
          border: `1px solid ${open ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: `${theme.radius.md}px`,
          bgcolor: 'background.paper',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 12,
          fontWeight: 600,
          color: theme.palette.text.primary,
          whiteSpace: 'nowrap',
          transition: 'border-color .12s',
          '&:hover': {
            borderColor: disabled ? theme.palette.divider : theme.palette.border.strong,
          },
          '&:disabled': { opacity: 0.45 },
        })}
      >
        <AccessTimeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
        {label}
        <KeyboardArrowDownIcon sx={{ fontSize: 12, color: 'text.secondary', ml: 'auto' }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{
          list: { 'aria-label': 'Notification cadence', sx: { minWidth: 150, py: 1 } },
          paper: { sx: { mt: 1, border: '1px solid', borderColor: 'divider', boxShadow: 7 } },
        }}
      >
        {CADENCES.map((cadence) => {
          const selected = value === cadence.id;
          return (
            <MenuItem
              key={cadence.id}
              role="menuitemradio"
              aria-checked={selected}
              selected={selected}
              onClick={() => {
                onChange(cadence.id);
                setAnchor(null);
              }}
              sx={{ minHeight: 34, fontSize: 12, fontWeight: selected ? 600 : 500 }}
            >
              <Typography sx={{ flex: 1, fontSize: 'inherit', fontWeight: 'inherit' }}>
                {cadence.label}
              </Typography>
              {selected && <CheckIcon sx={{ ml: 2, fontSize: 13, color: 'primary.main' }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
